import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export function Section({ title, children }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      {title && (
        <div className="card-header">
          <div className="card-title">{title}</div>
        </div>
      )}
      <div className="card-body">{children}</div>
    </div>
  );
}

export function F({ label, children, span2 }) {
  return (
    <div className={`form-group${span2 ? ' span2' : ''}`}>
      {label && <label>{label}</label>}
      {children}
    </div>
  );
}

export function I(props) {
  return <input {...props} />;
}

export function S({ options = [], ...props }) {
  return (
    <select {...props}>
      {options.map((opt, i) => {
        const val = typeof opt === 'object' ? opt.value : opt;
        const label = typeof opt === 'object' ? opt.label : String(opt);
        return <option key={i} value={val}>{label}</option>;
      })}
    </select>
  );
}

export function Modal({ title, onClose, maxWidth = 500, children }) {
  return createPortal(
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth }}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div style={{ padding: '18px 22px' }}>{children}</div>
      </div>
    </div>,
    document.body
  );
}

export function SectionBadge({ sec, idx, sections, setLocal }) {
  const remove = () => setLocal(l => ({ ...l, sections: sections.filter((_, i) => i !== idx) }));
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 20, fontSize: 12, color: '#1e40af', fontWeight: 500 }}>
      {sec}
      <button onClick={remove} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
    </span>
  );
}

export function AddSectionInput({ local, setLocal }) {
  const [val, setVal] = useState('');
  const add = () => {
    const trimmed = val.trim();
    if (!trimmed) return;
    const sections = local.sections || [];
    if (sections.includes(trimmed)) return;
    setLocal(l => ({ ...l, sections: [...(l.sections || []), trimmed] }));
    setVal('');
  };
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <input
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && add()}
        placeholder="Add section e.g. A, B, C..."
        style={{ flex: 1, padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 13 }}
      />
      <button className="btn btn-primary btn-sm" onClick={add}>+ Add</button>
    </div>
  );
}

// ─── TOAST ─────────────────────────────────────────────────
// Single shared copy — was duplicated verbatim across Fees/ and ParentPortal/,
// which meant the two copies could silently drift apart.
export const TOAST_ANIM = `@keyframes pillIn{from{opacity:0;transform:translateY(20px) scale(.9)}to{opacity:1;transform:translateY(0) scale(1)}}@keyframes dotPulse{0%,100%{opacity:1}50%{opacity:.3}}@keyframes shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-5px)}40%,80%{transform:translateX(5px)}}`;

export function Toast({ toasts, removeToast }) {
  const C = {
    error:   { bg:'#fef2f2', border:'#fca5a5', dot:'#ef4444', text:'#991b1b' },
    success: { bg:'#f0fdf4', border:'#86efac', dot:'#22c55e', text:'#14532d' },
    warn:    { bg:'#fffbeb', border:'#fde68a', dot:'#f59e0b', text:'#92400e' },
    info:    { bg:'#eef2ff', border:'#c7d2fe', dot:'#6366f1', text:'#3730a3' },
  };
  return (
    <div style={{ position:'fixed', bottom:20, left:'50%', transform:'translateX(-50%)', zIndex:9999, display:'flex', flexDirection:'column', gap:8, pointerEvents:'none', alignItems:'center' }}>
      {toasts.map(t => {
        const c = C[t.type] || C.info;
        return (
          <div key={t.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', borderRadius:100, background:c.bg, border:`1.5px solid ${c.border}`, boxShadow:`0 4px 20px rgba(0,0,0,0.12)`, animation:'pillIn .3s cubic-bezier(.175,.885,.32,1.275)', pointerEvents:'auto', maxWidth:420 }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:c.dot, flexShrink:0, animation:'dotPulse 1.5s infinite' }} />
            <span style={{ fontSize:12, fontWeight:700, color:c.text, maxWidth:340, lineHeight:1.4 }}>{t.message}</span>
            <button onClick={() => removeToast(t.id)} style={{ background:'rgba(0,0,0,0.06)', border:'none', borderRadius:'50%', color:c.text, cursor:'pointer', width:18, height:18, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginLeft:4 }}><X size={10}/></button>
          </div>
        );
      })}
    </div>
  );
}

export function useToast() {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);
  const removeToast = useCallback((id) => setToasts(prev => prev.filter(t => t.id !== id)), []);
  const toast = {
    success: (msg) => addToast(msg, 'success'),
    error:   (msg) => addToast(msg, 'error'),
    warn:    (msg) => addToast(msg, 'warn'),
    info:    (msg) => addToast(msg, 'info'),
  };
  return { toasts, removeToast, toast };
}
